import type { RefObject } from "react";

export type FormErrors<T extends string = string> = Partial<Record<T, string>>;

// Validate item form where quantity is a number
export function validateItemForm(
  itemName: string,
  quantity: number,
): FormErrors<"itemName" | "quantity"> {
  const errors: FormErrors<"itemName" | "quantity"> = {};
  if (!itemName.trim()) errors.itemName = "Item name is required";
  if (!Number.isFinite(quantity) || quantity <= 0) {
    errors.quantity = "Quantity must be a number greater than 0";
  }
  return errors;
}

// Validate shopping list item form where quantity is provided as a string
export function validateShoppingListItemForm(values: {
  itemName: string;
  quantity: string;
}): FormErrors<"itemName" | "quantity"> {
  const errors: FormErrors<"itemName" | "quantity"> = {};
  if (!values.itemName.trim()) errors.itemName = "Item name is required";
  const qStr = values.quantity.trim();
  const q = qStr === "" ? NaN : Number(qStr);
  if (qStr === "" || Number.isNaN(q) || !Number.isFinite(q) || q <= 0) {
    errors.quantity = "Quantity must be a number greater than 0";
  }
  return errors;
}

type ValidateAuthArgs = {
  email: string;
  password: string;
};

export type AuthFormErrors =
  | FormErrors<"email" | "password">
  | (FormErrors<"email" | "password"> & { global?: string });

// Basic email pattern used for simple client-side validation
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export function isValidEmail(email: string): boolean {
  return EMAIL_PATTERN.test(email.trim());
}

// Validate auth form for sign in / sign up, returning field or global errors
export function validateAuthForm(args: ValidateAuthArgs): AuthFormErrors {
  const { email, password } = args;
  const errors: AuthFormErrors = {};

  if (!email.trim()) {
    errors.email = "Email is required.";
  } else if (!EMAIL_PATTERN.test(email.trim())) {
    errors.email = "Please enter a valid email address.";
  }

  if (!password) {
    errors.password = "Password is required.";
  } else if (password.length < 6) {
    // project rule: require at least 6 characters
    errors.password = "Password must be at least 6 characters.";
  }

  return errors;
}

// Focus the first invalid field using provided refs and optional priority order
// Example: focusFirstInvalidField(errors, { itemName: itemRef }, ['itemName'])
export function focusFirstInvalidField<K extends string = string>(
  errors: FormErrors<K> | null | undefined,
  refs: Partial<Record<K, RefObject<HTMLInputElement | null>>>,
  order?: K[],
): void {
  if (!errors) return;
  const keysToCheck = order ?? (Object.keys(refs) as K[]);

  for (const key of keysToCheck) {
    const ref = refs[key];
    if (errors[key] && ref?.current) {
      try {
        ref.current.focus();
      } catch {
        // ignore focus failures in non-browser or test environments
      }
      return;
    }
  }
}

/**
 * Clears a single field's error from a FormErrors object.
 * Returns the same reference if the field had no error (avoids unnecessary re-renders).
 */
export function clearFieldError<T extends string>(
  prev: FormErrors<T>,
  field: T,
): FormErrors<T> {
  if (!prev[field]) return prev;
  const next = { ...prev };
  delete next[field];
  return next;
}
